import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Accordion } from "radix-ui";

import "./styles.css";
import useClickout from "@/modules/common/hooks/useClickout";
import { isSidebarRouteActive } from "@/utils/sidebarActiveRoute";
import { cn } from "@/libs/cn";
import Tooltip from "@/modules/common/components/files/Tooltip";

interface SidebarItem {
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  label: string;
  isExpanded?: boolean;
  route?: string;
  badgeCount?: number;
  onClick?: () => void;
  setIsExpanded?: (value: boolean) => void;
}
interface SidebarItemProps extends SidebarItem {
  subItems?: SidebarItem[];
}

const SidebarItem = ({ icon: Icon, label, isExpanded, badgeCount, onClick, route, subItems, setIsExpanded }: SidebarItemProps) => {
  const { triggerRef, elementRef, openedState, setOpenedState } = useClickout(false);

  const location = useLocation();
  const isActive = route && isSidebarRouteActive(route, location.pathname);
  const hasActiveSubItem = Boolean(subItems?.some((item) => item.route && isSidebarRouteActive(item.route, location.pathname)));

  useEffect(() => {
    if (!hasActiveSubItem) {
      setOpenedState(false);
    }
  }, [location.pathname, hasActiveSubItem, setOpenedState]);

  const isAccordionOpen = hasActiveSubItem || openedState;
  const skipOpenAnimationRef = useRef(isAccordionOpen);

  useEffect(() => {
    skipOpenAnimationRef.current = false;
  }, []);

  const badgeComponent = ({
    IconComponent,
    isExpanded,
  }: {
    IconComponent?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    isExpanded?: boolean;
  }) => {
    return IconComponent ? (
      <div className="relative w-4 h-4">
        <IconComponent className="w-4 h-4 transition-all duration-300 ease-in-out" />
        {!isExpanded && Number(badgeCount) > 0 && (
          <span className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-brand-primary-contrast text-brand-primary text-[10px] w-5 h-5 flex items-center justify-center rounded-full">
            {Number(badgeCount) > 99 ? "99+" : Number(badgeCount)}
          </span>
        )}
      </div>
    ) : (
      isExpanded && !openedState && (
        <div className="w-6 h-6">
          {Number(badgeCount) > 0 && (
            <span className="bg-brand-primary-contrast text-brand-primary text-[10px] w-6 h-6 flex items-center justify-center rounded-full">
              {Number(badgeCount) > 99 ? "99+" : Number(badgeCount)}
            </span>
          )}
        </div>
      )
    );
  };

  useEffect(() => {
    if (!isExpanded) setOpenedState(false);
  }, [isExpanded, setOpenedState]);

  return (
    <>
      {subItems ? (
        <Accordion.Root type="single" collapsible value={isAccordionOpen ? label : ""}>
          <Accordion.Item key={label} value={label} className="relative text-brand-secondary-contrast text-[13px]">
            <div ref={triggerRef}>
              <Accordion.Trigger
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  if (!isExpanded && setIsExpanded) {
                    e.preventDefault();
                    setIsExpanded(true);
                    return;
                  }
                }}
                className={cn(
                  "AccordionPrimitiveTrigger",
                  "group flex items-center justify-between rounded-lg cursor-pointer w-full transition-all duration-300 ease-in-out",
                  isExpanded ? "px-1 pt-3" : "p-0",
                )}
              >
                <div
                  className={cn(
                    "text-ui-text-muted uppercase",
                    "group flex items-center transition-all duration-300 ease-in-out",
                    isExpanded ? "flex items-center w-full" : "flex justify-center items-center w-10 h-10 mx-auto",
                  )}
                >
                  {Icon && badgeComponent({ IconComponent: Icon, isExpanded })}
                  {isExpanded && <p className="text-[14px] line-clamp-1 transition-all duration-300 ease-in-out truncate">{label}</p>}
                  {badgeComponent({ isExpanded })}
                </div>
              </Accordion.Trigger>
            </div>

            <Accordion.Content
              className={cn(
                "transition-all duration-300 ease-in-out",
                "data-[state='open']:rounded-lg AccordionPrimitiveContent",
                skipOpenAnimationRef.current && "AccordionPrimitiveContent--instant",
              )}
              ref={elementRef}
            >
              <div className="flex flex-col gap-1">
                {subItems.map(({ label, icon: Icon, onClick, route, badgeCount: subItemBadgeCount }: SidebarItem, idx: number) => {
                  const isActiveSub = route && isSidebarRouteActive(route, location.pathname);
                  const subItemContent = (
                    <div
                      className={cn(
                        "py-1.5 px-3 flex items-center gap-4 text-[13px] text-brand-secondary-contrast cursor-pointer",
                        isActiveSub
                          ? "bg-lime-400/80 text-black rounded-lg"
                          : "text-brand-secondary-contrast hover:bg-brand-secondary-contrast/10 hover:text-brand-secondary-contrast",
                      )}
                      key={idx}
                    >
                      <div className={cn("flex items-center text-[13px] gap-1", isActiveSub && "font-semibold")}>
                        {Icon && badgeComponent({ IconComponent: Icon, isExpanded })}
                        {label}
                      </div>
                      {Number(subItemBadgeCount) > 0 && (
                        <div className="w-6 h-6">
                          <span className="bg-brand-primary-contrast text-brand-primary text-[11px] w-6 h-6 flex items-center justify-center rounded-full">
                            {Number(subItemBadgeCount) > 99 ? "99+" : Number(subItemBadgeCount)}
                          </span>
                        </div>
                      )}
                    </div>
                  );

                  return route ? (
                    <Link key={label} to={route}>
                      {subItemContent}
                    </Link>
                  ) : (
                    <button key={label} type="button" className="text-left w-full" onClick={() => onClick?.()}>
                      {subItemContent}
                    </button>
                  );
                })}
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      ) : (
        <Tooltip label={label} side="right" disabled={isExpanded}>
          <Link to={route ?? ""}>
            <div
              className={cn(
                "group flex items-center space-x-2 cursor-pointer w-full transition-all duration-300 ease-in-out rounded-lg",
                isActive
                  ? "bg-lime-400/80 text-black rounded-lg"
                  : "text-brand-secondary-contrast hover:bg-brand-secondary-contrast/10 hover:text-brand-secondary-contrast",
                isExpanded ? "flex items-center space-x-2 w-full py-1.5 px-3" : "flex justify-center items-center w-10 h-10 mx-auto",
              )}
              onClick={onClick}
            >
              {Icon && badgeComponent({ IconComponent: Icon, isExpanded })}
              {isExpanded && <span className="transition-all duration-300 ease-in-out text-[13px] line-clamp-1 truncate">{label}</span>}
              {badgeComponent({ isExpanded })}
            </div>
          </Link>
        </Tooltip>
      )}
    </>
  );
};

export default SidebarItem;
